import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import './App.css';
import './i18n'; // Import i18n configuration

// Eagerly loaded components (needed immediately)
import Navigation from './components/Navigation';
import RouteFAB from './components/ui/RouteFAB';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { LanguageProvider } from './contexts/LanguageContext';

// Lazy-loaded page components (code splitting)
const Login = React.lazy(() => import('./components/Login'));
const Dashboard = React.lazy(() => import('./components/Dashboard'));
const MealPlans = React.lazy(() => import('./components/MealPlans'));
const SmartShoppingList = React.lazy(() => import('./components/SmartShoppingList'));
const Chores = React.lazy(() => import('./components/Chores'));
const ChoreEdit = React.lazy(() => import('./components/ChoreEdit'));
const FamilyManagement = React.lazy(() => import('./components/FamilyManagement'));
const RecipeManagement = React.lazy(() => import('./components/RecipeManagement'));
const RecipeDetail = React.lazy(() => import('./components/RecipeDetail'));
const RecipeAiModifyView = React.lazy(() => import('./components/RecipeAiModifyView'));
const RecipeAiGenerateView = React.lazy(() => import('./components/RecipeAiGenerateView'));
const MealPlanAiGenerateView = React.lazy(() => import('./components/MealPlanAiGenerateView'));
const Calendar = React.lazy(() => import('./components/Calendar'));
const UpdateShoppingFromMealPlan = React.lazy(() => import('./components/UpdateShoppingFromMealPlan'));
const InviteAccept = React.lazy(() => import('./components/InviteAccept'));
const SharedRecipe = React.lazy(() => import('./components/SharedRecipe'));

// Loading fallback component
const PageLoadingFallback: React.FC = () => (
  <div className="loading-screen">Loading...</div>
);
 

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }
  
  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

// Recipe Detail Wrapper Component
const RecipeDetailWrapper: React.FC = () => {
  const { recipeId } = useParams<{ recipeId: string }>();
  const navigate = useNavigate();
  
  if (!recipeId) {
    return <Navigate to="/recipes" replace />;
  }
  
  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };
  
  return (
    <>
      <Navigation />
      <main className="main-content">
        <RecipeDetail 
          recipeId={recipeId} 
          onBack={handleBack}
        />
        <RouteFAB />
      </main>
    </>
  );
};

// App Content Component
const AppContent: React.FC = () => {
  const { user, isLoading } = useAuth();
  // Signal to the watchdog that the app rendered
  React.useEffect(() => {
    (window as any).__APP_READY = true;
  }, []);

  return (
    <div className="App">
      <Suspense fallback={<PageLoadingFallback />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/invite/:token" element={<InviteAccept />} />
        <Route path="/shared/recipe/:shareToken" element={<SharedRecipe />} />
        
        {/* Protected routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <>
              <Navigation />
              <main className="main-content">
                <Dashboard />
                <RouteFAB />
              </main>
            </>
          </ProtectedRoute>
        } />
        <Route path="/meal-plans" element={
          <ProtectedRoute>
            <>
              <Navigation />
              <main className="main-content">
                <MealPlans />
                <RouteFAB />
              </main>
            </>
          </ProtectedRoute>
        } />
        <Route path="/meal-plans/:mealPlanId/update-shopping-list" element={
          <ProtectedRoute>
            <>
              <Navigation />
              <main className="main-content">
                <UpdateShoppingFromMealPlan />
                <RouteFAB />
              </main>
            </>
          </ProtectedRoute>
        } />
        <Route path="/meal-plans/ai-generate" element={
          <ProtectedRoute>
            <>
              <Navigation />
              <main className="main-content">
                <MealPlanAiGenerateView />
                <RouteFAB />
              </main>
            </>
          </ProtectedRoute>
        } />
        <Route path="/shopping-list" element={
          <ProtectedRoute>
            <>
              <Navigation />
              <main className="main-content">
                <SmartShoppingList />
                <RouteFAB />
              </main>
            </>
          </ProtectedRoute>
        } />
        <Route path="/chores" element={
          <ProtectedRoute>
            <>
              <Navigation />
              <main className="main-content">
                <Chores />
                <RouteFAB />
              </main>
            </>
          </ProtectedRoute>
        } />
        <Route path="/chores/:choreId/edit" element={
          <ProtectedRoute>
            <>
              <Navigation />
              <main className="main-content">
                <ChoreEdit />
                <RouteFAB />
              </main>
            </>
          </ProtectedRoute>
        } />
        <Route path="/family" element={
          <ProtectedRoute>
            <>
              <Navigation />
              <main className="main-content">
                <FamilyManagement />
                <RouteFAB />
              </main>
            </>
          </ProtectedRoute>
        } />
        <Route path="/recipes" element={
          <ProtectedRoute>
            <>
              <Navigation />
              <main className="main-content">
                <RecipeManagement familyId="current" />
                <RouteFAB />
              </main>
            </>
          </ProtectedRoute>
        } />
        <Route path="/recipes/:recipeId" element={
          <ProtectedRoute>
            <RecipeDetailWrapper />
          </ProtectedRoute>
        } />
        <Route path="/recipes/:recipeId/ai-modify" element={
          <ProtectedRoute>
            <>
              <Navigation />
              <main className="main-content">
                <RecipeAiModifyView />
                <RouteFAB />
              </main>
            </>
          </ProtectedRoute>
        } />
        <Route path="/recipes/ai-generate" element={
          <ProtectedRoute>
            <>
              <Navigation />
              <main className="main-content">
                <RecipeAiGenerateView />
                <RouteFAB />
              </main>
            </>
          </ProtectedRoute>
        } />
        <Route path="/calendar" element={
          <ProtectedRoute>
            <>
              <Navigation />
              <main className="main-content">
                <Calendar />
                <RouteFAB />
              </main>
            </>
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </div>
  );
};

function App() {
  return (
    <Router basename={process.env.PUBLIC_URL || '/'}>
      <ThemeProvider>
        <AuthProvider>
          <NotificationProvider>
            <LanguageProvider>
              <AppContent />
            </LanguageProvider>
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
